/**
 * JobsPage.jsx — Job Pipeline master-detail layout (MUI)
 *
 * Desktop: side-by-side 40% list / 60% detail
 * Mobile: full-width list OR full-width detail, driven by ?select= URL param
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, IconButton, Button, Badge } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import { useJobs } from '../hooks/useJobs';
import { untriagedCount } from '../lib/webLeads';
import { useContacts } from '../hooks/useContacts';
import { useMobile } from '../hooks/useMobile';
import { useFocus } from '../hooks/useFocus';
import JobList from '../components/jobs/JobList';
import JobsMoneyCounter from '../components/jobs/JobsMoneyCounter';
import JobDetail from '../components/jobs/JobDetail';
import QuickJobForm from '../components/jobs/QuickJobForm';
import JobForm from '../components/jobs/JobForm';

export default function JobsPage() {
  const { isMobile } = useMobile();
  const { isHidden } = useFocus();
  const navigate = useNavigate();
  const [showQuickForm, setShowQuickForm] = useState(false);
  const [showFullForm, setShowFullForm] = useState(false);
  const [formPrefill, setFormPrefill] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { data: allJobs = [] } = useJobs();
  const { data: allContacts = [] } = useContacts();
  const [searchParams, setSearchParams] = useSearchParams();
  // New web leads awaiting triage — drives the Inquiries entry affordance.
  const newLeadCount = useMemo(() => untriagedCount(allJobs), [allJobs]);

  // On mobile, ?select= param drives which view is shown and stays in URL for back-button
  // On desktop, we read it once and clear it (existing behavior)
  const selectParam = searchParams.get('select');

  // Desktop: local state for selected job; Mobile: derived from URL param
  const [desktopSelectedId, setDesktopSelectedId] = useState(null);

  const selectedJobId = isMobile ? selectParam : desktopSelectedId;
  const selectedJob = selectedJobId ? allJobs.find((j) => j.id === selectedJobId) || null : null;

  // Desktop: auto-select from URL param and clear it
  useEffect(() => {
    if (!isMobile && selectParam && allJobs.length > 0) {
      const found = allJobs.find((j) => j.id === selectParam);
      if (found) {
        setDesktopSelectedId(found.id);
        setShowQuickForm(false);
        setShowFullForm(false);
      }
      // Clear URL param on desktop (replace so back doesn't revisit)
      const next = new URLSearchParams(searchParams);
      next.delete('select');
      setSearchParams(next, { replace: true });
    }
  }, [selectParam, allJobs, isMobile, searchParams, setSearchParams]);

  // Handle job selection
  const handleSelect = useCallback(
    (job) => {
      if (isMobile) {
        // Push a new history entry so browser back returns to list
        navigate(`/jobs?select=${job.id}`);
      } else {
        setDesktopSelectedId(job?.id || null);
      }
      setShowQuickForm(false);
      setShowFullForm(false);
    },
    [isMobile, navigate],
  );

  const handleBack = useCallback(() => {
    if (isMobile) {
      // Use browser back if we have history, otherwise replace to list
      if (window.history.state?.idx > 0) navigate(-1);
      else navigate('/jobs', { replace: true });
    } else {
      setDesktopSelectedId(null);
    }
  }, [isMobile, navigate]);

  // ?action=newJob handling
  useEffect(() => {
    if (searchParams.get('action') === 'newJob') {
      const contactId = searchParams.get('contactId');
      const shootDate = searchParams.get('shootDate');
      const jobMode = searchParams.get('jobMode') || 'quick';
      const contact = contactId ? allContacts.find((c) => String(c.id) === contactId) : null;

      const prefill = contact ? {
        contactId: contact.id,
        clientName: contact.name || '',
        contactName: contact.name || '',
        contactEmail: contact.email || '',
        contactPhone: contact.phone || '',
      } : {};

      if (shootDate) prefill.shootDate = shootDate;
      if (jobMode === 'realestate' && prefill) prefill.jobType = 'Real Estate Video';

      if (jobMode === 'custom') {
        setShowFullForm(true);
        setShowQuickForm(false);
      } else {
        setShowQuickForm(true);
        setShowFullForm(false);
      }
      setFormPrefill(prefill);
      if (isMobile) {
        navigate('/jobs', { replace: true });
      } else {
        setDesktopSelectedId(null);
        const next = new URLSearchParams(searchParams);
        next.delete('action'); next.delete('shootDate'); next.delete('contactId'); next.delete('jobMode');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, allContacts, isMobile, navigate]);

  const handleQuickAdd = () => { setShowQuickForm(true); setShowFullForm(false); setFormPrefill(null); if (isMobile) navigate('/jobs', { replace: true }); else setDesktopSelectedId(null); };
  const handleFormSave = () => { setShowQuickForm(false); setShowFullForm(false); setFormPrefill(null); setRefreshTrigger((p) => p + 1); };
  const handleFormCancel = () => { setShowQuickForm(false); setShowFullForm(false); setFormPrefill(null); };
  const handleRefresh = () => setRefreshTrigger((p) => p + 1);

  // ── Mobile layout: list or detail ──────────────────────────────────────────
  if (isMobile) {
    const showDetail = selectedJob && !showQuickForm && !showFullForm;

    if (showDetail) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Mobile back bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={handleBack} sx={{ color: 'text.secondary' }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Jobs</Typography>
          </Box>
          <JobDetail job={selectedJob} onClose={handleBack} onRefresh={handleRefresh} key={`detail-${selectedJob.id}-${refreshTrigger}`} />
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* New-web-leads banner — only when something is waiting */}
        {!showQuickForm && !showFullForm && newLeadCount > 0 && (
          <Button
            fullWidth
            size="small"
            startIcon={<MoveToInboxOutlinedIcon />}
            onClick={() => navigate('/jobs/inquiries')}
            sx={{ mb: 1, justifyContent: 'flex-start', bgcolor: 'rgba(19,181,234,0.12)', textTransform: 'none' }}
          >
            {newLeadCount} new web {newLeadCount === 1 ? 'lead' : 'leads'} — review inquiries
          </Button>
        )}
        {showQuickForm ? (
          <QuickJobForm onSave={handleFormSave} onCancel={handleFormCancel} prefillContact={formPrefill} />
        ) : showFullForm ? (
          <JobForm prefill={formPrefill} onSave={handleFormSave} onCancel={handleFormCancel} />
        ) : (
          <>
            <Box sx={{ mb: 1 }}><JobsMoneyCounter /></Box>
            <JobList selectedJobId={null} onSelect={handleSelect} onQuickAdd={handleQuickAdd} key={`list-${refreshTrigger}`} />
          </>
        )}
      </Box>
    );
  }

  // ── Desktop layout: side-by-side ───────────────────────────────────────────
  return (
    <Box data-track-area="jobs" sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 8rem)', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>Jobs</Typography>
        {(newLeadCount > 0 || !isHidden('jobs.inquiriesButton')) && (
          <Badge data-focus="jobs.inquiriesButton" badgeContent={newLeadCount} color="warning" max={99}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MoveToInboxOutlinedIcon />}
              onClick={() => navigate('/jobs/inquiries')}
              sx={{ textTransform: 'none' }}
            >
              Inquiries
            </Button>
          </Badge>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Left panel 40% */}
        <Box sx={{ width: '40%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <JobsMoneyCounter />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <JobList selectedJobId={selectedJob?.id} onSelect={handleSelect} onQuickAdd={handleQuickAdd} key={`list-${refreshTrigger}`} />
          </Box>
        </Box>

        {/* Right panel 60% */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showQuickForm ? (
            <QuickJobForm onSave={handleFormSave} onCancel={handleFormCancel} prefillContact={formPrefill} />
          ) : showFullForm ? (
            <JobForm prefill={formPrefill} onSave={handleFormSave} onCancel={handleFormCancel} />
          ) : selectedJob ? (
            <JobDetail job={selectedJob} onClose={() => setDesktopSelectedId(null)} onRefresh={handleRefresh} key={`detail-${selectedJob.id}-${refreshTrigger}`} />
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No job selected</Typography>
                <Typography variant="caption" color="text.disabled">Select a job from the list or create a new one</Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}
